/**
 * Wording (MAP 249 spec §6, ticket 05): what each customer email says, the catalog of the seven
 * kinds a shop may reword, and the ONE function that turns a wording and a record into the words
 * a customer gets. Every send renders through `renderCustomerEmail`, and so does the edit sheet's
 * preview (`services/email-wording.ts`): the preview cannot describe an email the send would not
 * write.
 *
 * A wording is plain text with three things in it, and nothing else:
 *  - **fields** `{{customer}}`: filled in for each email. Each kind has its own set
 *    (`WORDING_CATALOG[kind].fields`); a field the kind does not have is refused when the wording
 *    is saved, and left as typed in a preview, never blanked (`LESSONS` 2026-07-31).
 *  - **optional text** `[, due {{due_date}}]`: printed without its brackets when every field in
 *    it has a value, left out when one has none. Brackets with no field in them are ordinary text.
 *  - **lines**: a line left with nothing on it (its optional text left out, its only field empty)
 *    goes, and blank lines in a row become one.
 * That is exactly enough to write today's five hard-coded emails WORD FOR WORD in every state of
 * their record (a due date or none, a title or none, a late fee or none, before or after the due
 * date), so nothing a customer gets changes until a shop edits.
 *
 * Two fields are ServWell's, never typed:
 *  - `{{link}}`, the kind's built link (the invoice's `/i/` page, the quote's `/q/` page, the
 *    review link). A kind that has one must keep it in its message.
 *  - `{{reply_line}}`, how the customer reaches the shop. Its words depend on whether a reply
 *    reaches the shop at all (a confirmed reply address, the shop's own domain or its mailbox):
 *    without one, "Reply to this email…" would sit above a footer saying replies don't reach the
 *    shop (01's validator, carried to 05), so it says "Questions? Contact {shop} directly." A
 *    queued email keeps it as a marker; the first freeze puts in the line for the route the email
 *    actually takes (fix-up r1 #3, `resolveReplyLine`).
 *
 * A saved wording is judged on EVERY way it can come out (fix-up r1 #1, #4: `wordingVariants`):
 * each field a record may leave empty, empty or not, its optional text and lines following. Its
 * link must stand outside any optional text (#2), no way may assemble a contact or a brand, empty
 * the subject or the message, or outgrow what an email can hold.
 *
 * ⚠️ Every default keeps the content check's posing words (`POSING_WORDS`: team, care, help,
 * billing…) more than three words from `{{shop}}`: for a shop whose brand-looking name staff have
 * cleared ("Norton Plumbing"), a posing word beside the name holds the email (ticket 03, fix-up
 * r2). `apps/api/test/email-wording.test.ts` renders every default against that shop.
 */
import { WINDOWED_EMAIL_KINDS, oneLine, type CustomerEmailKind } from './customer-emails';
import { MAX_SHOP_NAME } from './email-rules';
import { formatCents } from './money';
import { SERVICE_TYPE_LABEL_MAX } from './schemas/serviceType';

/** The kinds a shop may reword: the seven customer kinds (spec §6, ruling D). */
export const WORDING_KINDS = [
  'invoice',
  'quote',
  'payment_reminder',
  'quote_follow_up',
  'quote_approved',
  'review_request',
  'service_reminder',
] as const satisfies readonly CustomerEmailKind[];
export type WordingKind = (typeof WORDING_KINDS)[number];

/** Every field any kind has. Each kind has its own subset (`WordingKindInfo.fields`). */
export const MERGE_FIELD_NAMES = [
  'customer',
  'shop',
  'invoice_number',
  'balance',
  'due_date',
  'due_when',
  'late_fee_note',
  'quote_number',
  'quote_title',
  'total',
  'valid_until',
  'service',
  'months',
  'link',
  'reply_line',
] as const;
export type MergeFieldName = (typeof MERGE_FIELD_NAMES)[number];

/** The longest a saved subject may be (spec §6; the `email_wordings` CHECK). In characters. */
export const MAX_WORDING_SUBJECT = 150;
/** The longest a saved message may be (spec §6; the `email_wordings` CHECK). In characters. */
export const MAX_WORDING_BODY = 2000;

/** What a kind's built link opens: it names the link on the chip and in the refusal. */
export type WordingLink = 'invoice' | 'quote' | 'review';

export interface Wording {
  subject: string;
  body: string;
}

export interface WordingKindInfo {
  /** Its name in Settings. */
  label: string;
  /** When it goes, in the shop's words (the list in Settings shows it under the name). */
  timing: string;
  /** A promotional kind carries an unsubscribe link and respects the suppression list (§6). */
  promotional: boolean;
  /** Goes only inside the send window, 8 AM–7 PM shop time (spec §2 step 4). */
  windowed: boolean;
  /** Its built link (`{{link}}`), or none. */
  link: WordingLink | null;
  /** Its fields, in the order the edit sheet offers them. */
  fields: readonly MergeFieldName[];
  /** `{{reply_line}}` when a reply reaches the shop. */
  replyLine: string;
  /** `{{reply_line}}` when it does not. `{{shop}}` is the shop's name. */
  noReplyLine: string;
  /** ServWell's wording ("ours"): what goes until the shop saves its own. */
  wording: Wording;
}

const lines = (...l: string[]) => l.join('\n');

const INVOICE_FIELDS = [
  'customer',
  'shop',
  'invoice_number',
  'balance',
  'due_date',
] as const satisfies readonly MergeFieldName[];
const QUOTE_FIELDS = [
  'customer',
  'shop',
  'quote_number',
  'quote_title',
  'total',
  'valid_until',
] as const satisfies readonly MergeFieldName[];

const QUESTIONS = 'Questions? Contact {{shop}} directly.';
const WINDOWED: ReadonlySet<string> = new Set(WINDOWED_EMAIL_KINDS);

/**
 * The seven kinds (spec §6). The wordings of the first four are today's hard-coded emails (T §1.4)
 * moved here word for word; `{{reply_line}}` is the reply sentence each had, which now gives way
 * when no reply reaches the shop. The last three are new (ticket 06 sends them).
 */
export const WORDING_CATALOG: Record<WordingKind, WordingKindInfo> = {
  invoice: {
    label: 'Invoice',
    timing: 'When the office sends an invoice.',
    promotional: false,
    windowed: WINDOWED.has('invoice'),
    link: 'invoice',
    fields: [...INVOICE_FIELDS, 'link', 'reply_line'],
    replyLine: 'Reply to this email if anything looks wrong and we will sort it out.',
    noReplyLine: QUESTIONS,
    wording: {
      subject: 'INV-{{invoice_number}} from {{shop}}',
      body: lines(
        'Hi {{customer}},',
        '',
        'INV-{{invoice_number}} from {{shop}} comes to {{balance}}[, due {{due_date}}].',
        '',
        'You can see it here:',
        '{{link}}',
        '',
        '{{reply_line}}',
        '',
        '{{shop}}',
      ),
    },
  },
  quote: {
    label: 'Quote',
    timing: 'When the office sends a quote.',
    promotional: false,
    windowed: WINDOWED.has('quote'),
    link: 'quote',
    fields: [...QUOTE_FIELDS, 'link', 'reply_line'],
    replyLine: 'Or reply to this email with any questions.',
    noReplyLine: QUESTIONS,
    wording: {
      subject: 'Quote #{{quote_number}} from {{shop}}',
      body: lines(
        'Hi {{customer}},',
        '',
        'Quote #{{quote_number}} from {{shop}}[ — {{quote_title}}] comes to {{total}}.',
        '',
        '[This price holds until {{valid_until}}.]',
        '',
        'You can see it and approve it here:',
        '{{link}}',
        '',
        '{{reply_line}}',
        '',
        '{{shop}}',
      ),
    },
  },
  payment_reminder: {
    label: 'Payment reminder',
    timing: 'On the days set under Getting paid, between 8 AM and 7 PM.',
    promotional: false,
    windowed: WINDOWED.has('payment_reminder'),
    link: 'invoice',
    fields: [...INVOICE_FIELDS, 'due_when', 'late_fee_note', 'link', 'reply_line'],
    replyLine: 'Reply to this email if anything looks wrong and we will sort it out.',
    noReplyLine: QUESTIONS,
    // Today's reminder has no blank lines (they were filtered out with the empty late-fee line)
    // and no reply sentence: kept exactly so.
    wording: {
      subject: 'Invoice #{{invoice_number}} from {{shop}}',
      body: lines(
        'Hi {{customer}},',
        'Invoice #{{invoice_number}} for {{balance}} {{due_when}}.',
        '{{late_fee_note}}',
        'You can see it here:',
        '{{link}}',
        'Thanks,',
        '{{shop}}',
      ),
    },
  },
  quote_follow_up: {
    label: 'Quote follow-up',
    timing: 'On the days set under Automations, between 8 AM and 7 PM.',
    promotional: false,
    windowed: WINDOWED.has('quote_follow_up'),
    link: 'quote',
    fields: [...QUOTE_FIELDS, 'link', 'reply_line'],
    replyLine: 'If now is not the right time, just reply and let us know.',
    noReplyLine: QUESTIONS,
    wording: {
      subject: 'Following up on Quote #{{quote_number}} from {{shop}}',
      body: lines(
        'Hi {{customer}},',
        '',
        'Just checking in on Quote #{{quote_number}}[ — {{quote_title}}], which comes to {{total}}.',
        '',
        '[This price holds until {{valid_until}}.]',
        '',
        'You can see it and approve it here:',
        '{{link}}',
        '',
        '{{reply_line}}',
        '',
        '{{shop}}',
      ),
    },
  },
  quote_approved: {
    label: 'Quote approved',
    timing: 'As soon as a quote is approved, by the customer or the office.',
    promotional: false,
    windowed: WINDOWED.has('quote_approved'),
    link: 'quote',
    fields: [...QUOTE_FIELDS, 'link', 'reply_line'],
    replyLine: 'Reply to this email if you have any questions.',
    noReplyLine: QUESTIONS,
    wording: {
      subject: 'Quote #{{quote_number}} is approved',
      body: lines(
        'Hi {{customer}},',
        '',
        "Thank you for approving Quote #{{quote_number}}[ — {{quote_title}}], which comes to {{total}}. We'll be in touch about the next steps.",
        '',
        'You can see the quote here:',
        '{{link}}',
        '',
        '{{reply_line}}',
        '',
        '{{shop}}',
      ),
    },
  },
  review_request: {
    label: 'Review request',
    timing: 'At 10 AM the day after a job is closed.',
    promotional: true,
    windowed: WINDOWED.has('review_request'),
    link: 'review',
    fields: ['customer', 'shop', 'link', 'reply_line'],
    replyLine: "If anything wasn't right, just reply to this email.",
    noReplyLine: "If anything wasn't right, contact {{shop}} directly.",
    wording: {
      subject: 'How did we do?',
      body: lines(
        'Hi {{customer}},',
        '',
        'Thank you for choosing {{shop}}. If you have a minute, a short review of the work would mean a lot to us:',
        '{{link}}',
        '',
        '{{reply_line}}',
        '',
        '{{shop}}',
      ),
    },
  },
  service_reminder: {
    label: 'Service reminder',
    timing: 'When the months set on a service type have passed, between 8 AM and 7 PM.',
    promotional: true,
    windowed: WINDOWED.has('service_reminder'),
    // "Reply or call to book" (spec §6): no link.
    link: null,
    fields: ['customer', 'shop', 'service', 'months', 'reply_line'],
    replyLine: 'Reply to this email to book a visit.',
    noReplyLine: 'To book a visit, contact {{shop}} directly.',
    wording: {
      subject: 'Time for your next {{service}}?',
      body: lines(
        'Hi {{customer}},',
        '',
        "It's been {{months}} since your last {{service}}, so it may be time to book the next one.",
        '',
        '{{reply_line}}',
        '',
        '{{shop}}',
      ),
    },
  },
};

/** What each field stands for, as the edit sheet's chip says it. */
const FIELD_LABEL: Record<Exclude<MergeFieldName, 'link'>, string> = {
  customer: 'Your customer’s name',
  shop: 'Your shop’s name',
  invoice_number: 'The invoice number',
  balance: 'What they owe',
  due_date: 'The due date',
  due_when: 'When it’s due: “is due on …”, “is due today” or “was due on …”',
  late_fee_note: 'A line about the late fee, only when there is one',
  quote_number: 'The quote number',
  quote_title: 'The quote’s title, when it has one',
  total: 'The quote’s total',
  valid_until: 'The date the price holds until, when there is one',
  service: 'The service type',
  months: 'How long since their last one (“12 months”)',
  reply_line:
    'How they reach you: “Reply to this email…” when replies reach you, or “contact you directly” when they can’t',
};

const LINK_LABEL: Record<WordingLink, string> = {
  invoice: 'The link to their invoice page',
  quote: 'The link to their quote page, where they approve it',
  review: 'The link to your review page',
};

/** What a field of `kind` stands for: the chip's description. */
export function mergeFieldLabel(kind: WordingKind, name: MergeFieldName): string {
  if (name === 'link') {
    const link = WORDING_CATALOG[kind].link;
    return link ? LINK_LABEL[link] : 'A link';
  }
  return FIELD_LABEL[name];
}

// ── The record a kind is about ──────────────────────────────────────────────────────────

export interface InvoiceWordingFacts {
  /** Assigned when the invoice is issued; only issued invoices are emailed. */
  invoiceNumber: number | null;
  /** What they owe now: total, plus late fees, less what was paid. */
  balanceCents: number;
  dueDate: string | null;
  link: string;
}

export interface ReminderWordingFacts extends InvoiceWordingFacts {
  /** The reminder's step, in days from the due date: before it (< 0), on it (0), after it. */
  offsetDays: number;
  lateFeeCents: number;
}

export interface QuoteWordingFacts {
  quoteNumber: number;
  title: string | null;
  totalCents: number;
  validUntil: string | null;
  link: string;
}

export interface ReviewWordingFacts {
  /** The shop's review link (ticket 06: https, allow-listed). */
  link: string;
}

export interface ServiceReminderWordingFacts {
  /** The service type's name. */
  service: string;
  /** Its "remind after" months. */
  months: number;
}

export interface WordingRecordFacts {
  invoice: InvoiceWordingFacts;
  payment_reminder: ReminderWordingFacts;
  quote: QuoteWordingFacts;
  quote_follow_up: QuoteWordingFacts;
  quote_approved: QuoteWordingFacts;
  review_request: ReviewWordingFacts;
  service_reminder: ServiceReminderWordingFacts;
}

/** A kind and the facts of its record, together (so each kind's facts are checked). */
export type WordingFacts = {
  [K in WordingKind]: { kind: K; facts: WordingRecordFacts[K] };
}[WordingKind];

/** Who the email is between. */
export interface EmailParties {
  /** The customer's name as it is on file. */
  customer: string;
  /** The shop's name, in full. */
  shop: string;
  /**
   * Does a reply reach the shop? (A confirmed reply address, its domain, or its mailbox.) Null:
   * decided when the email goes (fix-up r1 #3): `{{reply_line}}` stays a marker
   * (`REPLY_LINE_MARKER`) that the first freeze resolves for the route the email actually takes.
   */
  replies: boolean | null;
}

/**
 * What a queued email carries where `{{reply_line}}` goes until it is sent (fix-up r1 #3). A reply
 * path is the ROUTE's (the ServWell address with a confirmed reply address, the shop's domain, its
 * mailbox), and the route is chosen when the email goes, not when it was written: the freeze puts
 * the line in (`resolveReplyLine`), so the words agree with the footer and the Reply-To they go
 * with.
 *
 * ⚠️ UNFORGEABLE (fix-up r2 #2): it is fenced by a private-use code point (U+E000) that no record
 * value can carry (`mergeValues` strips it from every value) and no wording may hold (the save
 * refuses private-use characters). So only the renderer's own `{{reply_line}}` ever becomes a
 * reply line: a customer named "{{reply_line}}" stays exactly that.
 */
export const REPLY_LINE_MARKER = '\uE000reply_line\uE000';

/** The marker's fence: stripped from every record value before it is put in. */
const MARKER_FENCE = /\uE000/g;

/** The reply line of `kind` for a shop called `shop`, with a reply path or without one. */
export function replyLineFor(kind: WordingKind, shop: string, replies: boolean): string {
  const info = WORDING_CATALOG[kind];
  return replies
    ? info.replyLine
    : renderWording(info.noReplyLine, new Map([['shop', shop]]));
}

/** `body` (a queued email of `kind`) with its reply line put in where the marker is. */
export function resolveReplyLine(
  kind: WordingKind,
  body: string,
  shop: string,
  replies: boolean,
): string {
  return body.split(REPLY_LINE_MARKER).join(replyLineFor(kind, shop, replies));
}

/**
 * Every way `body` can go: with each reply line, when it has the marker. The content check reads
 * them all (fix-up r1 #3), so the one the freeze picks has been read whatever the route.
 */
export function replyLineVariants(
  kind: WordingKind,
  body: string,
  shop: string,
): string[] {
  if (!body.includes(REPLY_LINE_MARKER)) return [body];
  return [true, false].map((replies) => resolveReplyLine(kind, body, shop, replies));
}

/** Is `kind` one of the seven worded kinds (not a ticket email, not an invite)? */
export function isWordingKind(kind: string): kind is WordingKind {
  return (WORDING_KINDS as readonly string[]).includes(kind);
}

/**
 * A field's value. Null or empty = it has none: optional text around it is left out (an empty
 * quote title is no title, as it always was).
 */
export type MergeValues = ReadonlyMap<string, string | null>;

const LATE_FEE_NOTE =
  'That figure includes a late fee that has been added to the balance.';

/** Every field of the email's kind, with this record's value (`renderWording`'s input). */
export function mergeValues(parties: EmailParties, input: WordingFacts): MergeValues {
  const values = new Map<string, string | null>([
    ['customer', parties.customer],
    ['shop', parties.shop],
    [
      'reply_line',
      parties.replies === null
        ? REPLY_LINE_MARKER
        : replyLineFor(input.kind, parties.shop, parties.replies),
    ],
  ]);
  switch (input.kind) {
    case 'invoice':
    case 'payment_reminder': {
      const f = input.facts;
      values.set(
        'invoice_number',
        f.invoiceNumber === null ? null : String(f.invoiceNumber),
      );
      values.set('balance', formatCents(f.balanceCents));
      values.set('due_date', f.dueDate);
      values.set('link', f.link);
      if (input.kind === 'payment_reminder') {
        const { offsetDays, lateFeeCents } = input.facts;
        const due = input.facts.dueDate ?? '';
        values.set(
          'due_when',
          offsetDays < 0
            ? `is due on ${due}`
            : offsetDays === 0
              ? 'is due today'
              : `was due on ${due}`,
        );
        values.set('late_fee_note', lateFeeCents > 0 ? LATE_FEE_NOTE : null);
      }
      break;
    }
    case 'quote':
    case 'quote_follow_up':
    case 'quote_approved': {
      const f = input.facts;
      values.set('quote_number', String(f.quoteNumber));
      values.set('quote_title', f.title);
      values.set('total', formatCents(f.totalCents));
      values.set('valid_until', f.validUntil);
      values.set('link', f.link);
      break;
    }
    case 'review_request':
      values.set('link', input.facts.link);
      break;
    case 'service_reminder': {
      const { service, months } = input.facts;
      values.set('service', service);
      values.set('months', `${months} month${months === 1 ? '' : 's'}`);
      break;
    }
  }
  // A record's values are put in as they are, except that they cannot carry the reply line's
  // fence (fix-up r2 #2): only the renderer's own marker is ever resolved.
  for (const [name, value] of values) {
    if (name !== 'reply_line' && value) values.set(name, value.replace(MARKER_FENCE, ''));
  }
  return values;
}

// ── The engine ──────────────────────────────────────────────────────────────────────────

/** `{{ name }}`: a field (case and inner spaces ignored, as the campaign fields always were). */
const FIELD = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;
/** Anything in double braces, well formed or not: what a save reads for fields it doesn't know. */
const BRACED = /\{\{([^{}]*)\}\}/g;
/** Optional text: `[` … `]` on one line, with no bracket inside it. */
const OPTIONAL = /\[([^[\]\n]*)\]/g;

const filled = (v: string | null | undefined): boolean =>
  v !== null && v !== undefined && v !== '';

/**
 * `template` with `values` put in (the rules in the module note). A field `values` does not hold
 * is left exactly as typed; a value is put in as it is, never read for fields or brackets.
 */
export function renderWording(template: string, values: MergeValues): string {
  return renderWordingParts(template, values).text;
}

/** A field token, whole (`FIELD`, kept by `split`). */
const FIELD_WHOLE = /(\{\{\s*[A-Za-z0-9_]+\s*\}\})/;

/**
 * `renderWording`, with the same text again in which every character of the WORDING is a space and
 * every character a value put in is itself (fix-up r3): `alone` lines up with `text`, character for
 * character, so a contact found in `text` and not in `alone` needs the wording's characters to
 * exist. Line breaks stay line breaks.
 */
export function renderWordingParts(
  template: string,
  values: MergeValues,
): { text: string; alone: string } {
  const blank = (s: string) => s.replace(/[^\n]/g, ' ');
  const out: { text: string; alone: string }[] = [];
  for (const line of template.replace(/\r\n?/g, '\n').split('\n')) {
    const kept = line.replace(OPTIONAL, (whole: string, inner: string) => {
      const names = [...inner.matchAll(FIELD)]
        .map((m) => m[1]!.toLowerCase())
        .filter((name) => values.has(name));
      if (names.length === 0) return whole;
      return names.every((name) => filled(values.get(name))) ? inner : '';
    });
    let text = '';
    let alone = '';
    kept.split(FIELD_WHOLE).forEach((piece, i) => {
      const key = i % 2 === 1 ? piece.replace(/[{}\s]/g, '').toLowerCase() : null;
      if (key !== null && values.has(key)) {
        const value = values.get(key) ?? '';
        text += value;
        alone += value;
      } else {
        text += piece;
        alone += blank(piece);
      }
    });
    // A line that had something on it and has nothing left goes.
    if (line.trim() !== '' && text.trim() === '') continue;
    out.push({ text, alone });
  }
  // Blank lines in a row become one; none at either end.
  const kept: { text: string; alone: string }[] = [];
  for (const l of out) {
    const isBlank = l.text.trim() === '';
    if (isBlank && (kept.length === 0 || kept[kept.length - 1]!.text === '')) continue;
    kept.push(isBlank ? { text: '', alone: '' } : l);
  }
  while (kept.length > 0 && kept[kept.length - 1]!.text === '') kept.pop();
  return {
    text: kept.map((l) => l.text).join('\n'),
    alone: kept.map((l) => l.alone).join('\n'),
  };
}

/**
 * The words one customer gets: the shop's wording (or ours, with none) for this record. The subject
 * is one line here; the queue caps it (`emailSubject`). The footer is the route's, added when the
 * email is sent.
 */
export function renderCustomerEmail(
  input: WordingFacts,
  parties: EmailParties,
  wording: Wording | null,
): Wording {
  const { subject, body } = wording ?? WORDING_CATALOG[input.kind].wording;
  const values = mergeValues(parties, input);
  return {
    subject: renderWording(subject, values),
    body: renderWording(body, values),
  };
}

/** The words of `renderCustomerEmail`, each with its values-alone twin (`renderWordingParts`). */
export interface CustomerEmailParts extends Wording {
  subjectAlone: string;
  bodyAlone: string;
}

/** `renderCustomerEmail`, with each part's values-alone twin (fix-up r3, the runtime check). */
export function renderCustomerEmailParts(
  input: WordingFacts,
  parties: EmailParties,
  wording: Wording | null,
): CustomerEmailParts {
  const { subject, body } = wording ?? WORDING_CATALOG[input.kind].wording;
  const values = mergeValues(parties, input);
  const s = renderWordingParts(subject, values);
  const b = renderWordingParts(body, values);
  return { subject: s.text, subjectAlone: s.alone, body: b.text, bodyAlone: b.alone };
}

// ── What a shop may save ────────────────────────────────────────────────────────────────

/** Characters counted as Postgres's `char_length` counts them: by code point. */
const charLength = (s: string): number => Array.from(s).length;

/**
 * Control characters a message may not hold (a tab and a line break are fine), and private-use
 * characters: the reply line's marker is fenced by one (fix-up r2 #2).
 */
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u0008\u000b-\u001f\u007f-\u009f\p{Co}]/u;

/** A subject as it is saved: one line, trimmed (every subject's rule, `oneLine`). */
export function normalizeWordingSubject(subject: string): string {
  return oneLine(subject);
}

/** A message as it is saved: one kind of line break, no blank lines before it, nothing after. */
export function normalizeWordingBody(body: string): string {
  return body
    .replace(/\r\n?/g, '\n')
    .replace(/^(?:[ \t]*\n)+/, '')
    .trimEnd();
}

/** Does `text` use the field `name`? */
function usesField(text: string, name: MergeFieldName): boolean {
  for (const m of text.matchAll(FIELD)) if (m[1]!.toLowerCase() === name) return true;
  return false;
}

/** What the customer does with the link: the refusal's reason to keep it. */
const LINK_PURPOSE: Record<WordingLink, string> = {
  invoice: 'it’s how your customer opens the invoice',
  quote: 'it’s how your customer opens the quote and approves it',
  review: 'it’s how your customer gets to your review page',
};

/**
 * Why this wording can't be saved for `kind`, as the sentence the owner reads, or null. The form
 * of it: something in both parts, the lengths (spec §6), no control characters, only the kind's
 * own fields, no stray braces, and the kind's link kept. Takes the NORMALIZED wording. The
 * content rules (no contact details, no brands: `wordingProblem`, ticket 03) are the server's next
 * check.
 */
export function wordingFormProblem(kind: WordingKind, wording: Wording): string | null {
  const info = WORDING_CATALOG[kind];
  if (wording.subject.trim() === '') return 'Give it a subject.';
  if (wording.body.trim() === '') return 'Give it a message.';
  const subjectLength = charLength(wording.subject);
  if (subjectLength > MAX_WORDING_SUBJECT) {
    return `A subject can be at most ${MAX_WORDING_SUBJECT} characters. This one has ${subjectLength}.`;
  }
  const bodyLength = charLength(wording.body);
  if (bodyLength > MAX_WORDING_BODY) {
    return `A message can be at most ${MAX_WORDING_BODY.toLocaleString('en-US')} characters. This one has ${bodyLength.toLocaleString('en-US')}.`;
  }
  if (CONTROL.test(wording.subject) || CONTROL.test(wording.body)) {
    return 'Take out the hidden control characters: wording is plain text.';
  }
  const own = new Set<string>(info.fields);
  for (const [where, text] of [
    ['subject', wording.subject],
    ['message', wording.body],
  ] as const) {
    for (const m of text.matchAll(BRACED)) {
      const name = m[1]!.trim();
      if (!own.has(name.toLowerCase())) {
        return `This email has no field “{{${name}}}”. Use one of the fields offered for it, or take it out of the ${where}.`;
      }
    }
    if (/\{\{|\}\}/.test(text.replace(BRACED, ''))) {
      return `Take out the stray “{{” or “}}” in the ${where}: a field is written like {{customer}}.`;
    }
  }
  // The reply line is a sentence of its own, put in when the email goes (fix-up r1 #3).
  if (usesField(wording.subject, 'reply_line')) {
    return 'Take {{reply_line}} out of the subject: it is a sentence for the message.';
  }
  // The link must be there whatever the record holds (fix-up r1 #2): optional text with a field in
  // it can be left out, so a link inside one does not count.
  if (info.link && !usesField(wording.body.replace(OPTIONAL_WITH_FIELD, ''), 'link')) {
    return `Keep {{link}} in the message, outside any [square brackets]: ${LINK_PURPOSE[info.link]}.`;
  }
  return null;
}

/** Optional text that can be left out: brackets with a field in them (`renderWording`'s rule). */
const OPTIONAL_WITH_FIELD = /\[[^[\]\n]*\{\{[^{}]*\}\}[^[\]\n]*\]/g;

// ── Every way a wording can come out (fix-up r1 #1, #4) ─────────────────────────────────

/**
 * The fields a record may leave EMPTY: an invoice with no due date, a quote with no title or no
 * date it holds until, a reminder with no late fee. Every other field always has a value (a
 * customer's name is required, an issued invoice has a number, a link is built). Optional text
 * is decided by these alone, so they are a wording's only free choices.
 */
export const NULLABLE_FIELDS: ReadonlySet<MergeFieldName> = new Set<MergeFieldName>([
  'due_date',
  'late_fee_note',
  'quote_title',
  'valid_until',
]);

/** The longest an email's body may be: `customer_emails_body_check` (0045). */
export const MAX_EMAIL_BODY = 20_000;

/**
 * The most characters each field can put into an email: the limits its values are written under
 * (a customer's name, a shop's name, a service type's label, a quote's title), and generous
 * bounds for the rest. A review link (ticket 06) must be kept within its bound.
 */
export const MERGE_FIELD_MAX: Record<MergeFieldName, number> = {
  customer: 200,
  shop: MAX_SHOP_NAME,
  invoice_number: 12,
  balance: 24,
  due_date: 10,
  due_when: 24,
  late_fee_note: LATE_FEE_NOTE.length,
  quote_number: 12,
  quote_title: 200,
  total: 24,
  valid_until: 10,
  service: SERVICE_TYPE_LABEL_MAX,
  months: 9,
  link: 500,
  reply_line: 160,
};

/** One way a wording can come out, and which fields were empty in it. */
export interface WordingVariant {
  /** The words, each field that has a value still written as itself (`{{customer}}`). */
  wording: Wording;
  empty: MergeFieldName[];
}

/**
 * EVERY way `wording` can come out for `kind` (fix-up r1 #1): each nullable field it uses empty or
 * not, with its optional text and its lines following, as `renderWording` decides. A field with a
 * value stays written as itself, so the content rules read it as they always have (a digit where
 * a contact could be completed, a word break for names). At most 2^4 ways, however many brackets.
 * The first is every field with a value: the wording as it was typed, without its brackets.
 */
export function wordingVariants(kind: WordingKind, wording: Wording): WordingVariant[] {
  const fields = WORDING_CATALOG[kind].fields;
  const used = (name: MergeFieldName) =>
    usesField(wording.subject, name) || usesField(wording.body, name);
  const nullable = fields.filter((f) => NULLABLE_FIELDS.has(f) && used(f));
  const out: WordingVariant[] = [];
  for (let mask = 0; mask < 1 << nullable.length; mask++) {
    const empty = nullable.filter((_, i) => mask & (1 << i));
    const values = new Map<string, string | null>(
      fields.map((f) => [f, empty.includes(f) ? null : `{{${f}}}`]),
    );
    out.push({
      wording: {
        subject: renderWording(wording.subject, values),
        body: renderWording(wording.body, values),
      },
      empty,
    });
  }
  return out;
}

/** "(It comes out that way when {{due_date}} is empty.)": where a variant's problem comes from. */
export function whenEmpty(empty: readonly MergeFieldName[]): string {
  if (empty.length === 0) return '';
  const list = empty.map((f) => `{{${f}}}`);
  const named =
    list.length === 1 ? list[0] : `${list.slice(0, -1).join(', ')} and ${list.at(-1)}`;
  return ` (It comes out that way when ${named} ${list.length === 1 ? 'is' : 'are'} empty.)`;
}

/**
 * Why `wording` could make an email the outbox cannot hold, in words, or null (fix-up r1 #4): a
 * subject or message that comes out empty for some record, or a message that, with every field at
 * its longest, is past `MAX_EMAIL_BODY`. Takes the NORMALIZED wording, after `wordingFormProblem`.
 */
export function wordingShapeProblem(kind: WordingKind, wording: Wording): string | null {
  for (const { wording: w, empty } of wordingVariants(kind, wording)) {
    if (oneLine(w.subject) === '') {
      return `The subject comes out empty when ${empty.map((f) => `{{${f}}}`).join(' and ')} ${empty.length === 1 ? 'is' : 'are'} empty: add words that are always there.`;
    }
    if (w.body.trim() === '') {
      return `The message comes out empty when ${empty.map((f) => `{{${f}}}`).join(' and ')} ${empty.length === 1 ? 'is' : 'are'} empty: add words that are always there.`;
    }
  }
  const longest = renderWording(
    wording.body,
    new Map(WORDING_CATALOG[kind].fields.map((f) => [f, 'x'.repeat(MERGE_FIELD_MAX[f])])),
  );
  const n = charLength(longest);
  if (n > MAX_EMAIL_BODY) {
    return `With its fields filled in, this message can come out at ${n.toLocaleString('en-US')} characters, past the ${MAX_EMAIL_BODY.toLocaleString('en-US')} an email can hold: use fewer fields.`;
  }
  return null;
}

/**
 * Why these RENDERED words can't be queued, or null: an empty subject or message, or a message past
 * `MAX_EMAIL_BODY` (the outbox's CHECKs). The composition checks every email with it (fix-up r1
 * #4): a shop's wording that comes out so for one record is not used for it; ours is.
 *
 * The body is measured as it will GO (fix-up r2 #2): each reply-line marker counted at the longest
 * reply line there can be, so whichever line the freeze puts in, the email fits.
 */
export function renderedProblem(words: Wording): string | null {
  if (oneLine(words.subject) === '') return 'its subject comes out empty';
  if (words.body.trim() === '') return 'its message comes out empty';
  const longest = words.body
    .split(REPLY_LINE_MARKER)
    .join('x'.repeat(MERGE_FIELD_MAX.reply_line));
  if (charLength(longest) > MAX_EMAIL_BODY) return 'its message comes out too long';
  return null;
}
